import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
} from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import { BaseMediaLibraryProps, MediaFileProps } from './types'
import { isFileMatch } from './utils/helpers'

export type MediaLibraryContextValue = Omit<
	BaseMediaLibraryProps,
	'onUpload'
> & {
	// Selection state
	selectedFiles: MediaFileProps[]
	selectedFolder: string | null
	bulkSelect: boolean

	// Drag and upload modal
	getRootProps: ReturnType<typeof useDropzone>['getRootProps']
	getInputProps: ReturnType<typeof useDropzone>['getInputProps']
	isDragging: boolean
	droppedFiles: File[]
	rejectedFiles: FileRejection[]
	clearRejectedFiles: () => void
	uploadModalVisible: boolean

	// Handlers (children can call these)
	handleCancelUploadModal: () => void
	handleOpenUploadModal: () => void
	handleSelectFile: (file: MediaFileProps) => void
	handleSelectFolder: (folder: string | null) => void
	handleCloseMediaPreview: () => void
	handleBulkSelectClick: () => void
	handleUploadSubmit: (files: File[]) => void
}

const MediaLibraryContext = createContext<
	MediaLibraryContextValue | undefined
>(undefined)

export const useMediaLibrary = (): MediaLibraryContextValue => {
	const ctx = useContext(MediaLibraryContext)
	if (!ctx) {
		throw new Error(
			'useMediaLibrary must be used within a MediaLibraryProvider'
		)
	}
	return ctx
}

// Provider component with internal state to reduce value prop usage in MediaLibrary.tsx
export const MediaLibraryProvider: React.FC<
	BaseMediaLibraryProps & {
		children: React.ReactNode
	}
> = ({
	children,
	data,
	folders,
	title,
	loading,
	selectActions,
	bulkSelectActions,
	onRefreshClick,
	onUpload,
	onSelect,
	onCloseMediaPreview,
	accept,
	preSelectedFiles = [],
}) => {
	const [selectedFiles, setSelectedFiles] =
		React.useState<MediaFileProps[]>(preSelectedFiles)
	const [selectedFolder, setSelectedFolder] = React.useState<
		string | null
	>(null)
	const [bulkSelect, setBulkSelect] = React.useState<boolean>(false)
	const [isDragging, setIsDragging] = React.useState<boolean>(false)
	const [droppedFiles, setDroppedFiles] = React.useState<File[]>([])
	const [uploadModalVisible, setUploadModalVisible] =
		React.useState<boolean>(false)
	const [rejectedFiles, setRejectedFiles] = React.useState<
		FileRejection[]
	>([])

	useEffect(() => {
		setSelectedFiles(preSelectedFiles)
	}, [preSelectedFiles])

	const computedBulkSelectActions = React.useMemo(() => {
		return bulkSelectActions?.map((action) => ({
			...action,
			text: `${action.text} (${selectedFiles.length})`,
			disabled: selectedFiles.length === 0,
		}))
	}, [bulkSelectActions, selectedFiles.length])

	const clearSelectedFiles = useCallback(() => {
		setSelectedFiles([])
	}, [])

	const clearDroppedFiles = () => {
		setDroppedFiles([])
	}

	const clearRejectedFiles = () => {
		setRejectedFiles([])
	}

	const handleCancelUploadModal = () => setUploadModalVisible(false)

	const handleOpenUploadModal = () => {
		setDroppedFiles([])
		setUploadModalVisible(true)
	}

	const handleDragEnter = () => setIsDragging(true)
	const handleDragLeave = () => setIsDragging(false)

	const handleDrop = (acceptedFiles: File[]) => {
		setIsDragging(false)
		setDroppedFiles(acceptedFiles)
		clearRejectedFiles()
		setUploadModalVisible(true)
	}

	const handleDropError = () => {}

	const handleDropRejected = useCallback(
		(fileRejections: FileRejection[]) => {
			clearDroppedFiles()
			setRejectedFiles(
				fileRejections.map((fileRejection) => fileRejection)
			)
			setUploadModalVisible(true)
			handleDropError()
		},
		[]
	)

	const { getRootProps, getInputProps } = useDropzone({
		noClick: true,
		onDragEnter: handleDragEnter,
		onDragLeave: handleDragLeave,
		onDrop: handleDrop,
		onDropRejected: handleDropRejected,
		accept,
	})

	const handleSelectFile = useCallback(
		(file: MediaFileProps) => {
			if (!bulkSelectActions || !bulkSelect) {
				setSelectedFiles([file])
				onSelect?.([file], bulkSelect)
				return
			}

			if (
				selectedFiles.some((selectedFile) =>
					isFileMatch(file, selectedFile)
				)
			) {
				setSelectedFiles((prev) => {
					const newFiles = prev.filter(
						(selectedFile) => !isFileMatch(file, selectedFile)
					)
					onSelect?.(newFiles, bulkSelect)
					return newFiles
				})
				return
			}

			setSelectedFiles((prev) => {
				const newFiles = [...prev, file]
				onSelect?.(newFiles, bulkSelect)
				return newFiles
			})
		},
		[bulkSelect, bulkSelectActions, onSelect, selectedFiles]
	)

	const handleSelectFolder = useCallback(
		(folder: string | null) => {
			if (folder === selectedFolder) {
				setSelectedFolder(null)
				return
			}
			setSelectedFolder(folder)
		},
		[selectedFolder]
	)

	const handleCloseMediaPreview = useCallback(() => {
		clearSelectedFiles()
		onCloseMediaPreview?.()
	}, [clearSelectedFiles, onCloseMediaPreview])

	const handleBulkSelectClick = useCallback(() => {
		clearSelectedFiles()
		setBulkSelect((prev) => !prev)
	}, [clearSelectedFiles])

	const handleUploadSubmit = useCallback(
		(files: File[]) => {
			onUpload?.(files)
			clearDroppedFiles()
			setUploadModalVisible(false)
		},
		[onUpload]
	)

	const value: MediaLibraryContextValue = useMemo(
		() => ({
			data,
			folders,
			title,
			loading,
			accept,
			onRefreshClick,
			selectedFiles,
			selectedFolder,
			bulkSelect,
			selectActions,
			bulkSelectActions: computedBulkSelectActions,
			getRootProps,
			getInputProps,
			isDragging,
			droppedFiles,
			rejectedFiles,
			clearRejectedFiles,
			uploadModalVisible,
			handleCancelUploadModal,
			handleOpenUploadModal,
			handleSelectFile,
			handleSelectFolder,
			handleCloseMediaPreview,
			handleBulkSelectClick,
			handleUploadSubmit,
		}),
		[
			data,
			folders,
			title,
			loading,
			accept,
			onRefreshClick,
			selectedFiles,
			selectedFolder,
			bulkSelect,
			selectActions,
			computedBulkSelectActions,
			getRootProps,
			getInputProps,
			isDragging,
			droppedFiles,
			rejectedFiles,
			uploadModalVisible,
			handleSelectFile,
			handleSelectFolder,
			handleCloseMediaPreview,
			handleBulkSelectClick,
			handleUploadSubmit,
		]
	)

	return (
		<MediaLibraryContext.Provider value={value}>
			{children}
		</MediaLibraryContext.Provider>
	)
}
